import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { analyzeInvoiceLocally } from '../../lib/invoiceOcr';

const NEW_PRODUCT = '__new__';
const MAX_FILES = 4;

const normalizeText = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const eligibleProduct = (product) => {
  const type = String(product?.type || 'final').toLowerCase();
  return !product?.deleted && type !== 'receta' && !product?.config?.isIceCream && !product?.excludeFromEntries;
};

const similarity = (left, right) => {
  const a = normalizeText(left);
  const b = normalizeText(right);
  if (!a || !b) return 0;
  if (a === b) return 1;
  if (a.includes(b) || b.includes(a)) return Math.min(a.length, b.length) / Math.max(a.length, b.length) + 0.15;
  const aTokens = new Set(a.split(' '));
  const bTokens = new Set(b.split(' '));
  const common = [...aTokens].filter(token => bTokens.has(token)).length;
  const union = new Set([...aTokens, ...bTokens]).size;
  return union ? common / union : 0;
};

const findMatch = (description, products) => {
  let best = null;
  let score = 0;
  for (const product of products) {
    const candidateScore = similarity(description, product.name);
    if (candidateScore > score) {
      best = product;
      score = candidateScore;
    }
  }
  if (!best || score < 0.58) return { product: null, score, status: 'new' };
  return { product: best, score, status: score >= 0.86 ? 'matched' : 'possible' };
};

const money = (value) => Math.round(Number(value) || 0).toLocaleString('es-CO');
const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const normalizeUnit = (unit) => {
  const value = normalizeText(unit);
  if (['g', 'gr', 'gramo', 'gramos', 'kg', 'kilo', 'kilos'].includes(value)) return value.startsWith('k') ? 'kg' : 'g';
  if (['ml', 'mililitro', 'mililitros', 'litro', 'litros', 'l'].includes(value)) return value.startsWith('l') ? 'l' : 'ml';
  if (['paquete', 'paquetes', 'pq', 'caja', 'cajas'].includes(value)) return value.startsWith('caj') ? 'caja' : 'paquete';
  return 'unidad';
};

const statusStyle = {
  matched: 'bg-emerald-100 text-emerald-800',
  possible: 'bg-amber-100 text-amber-800',
  new: 'bg-sky-100 text-sky-800',
};

const statusLabel = {
  matched: 'Existe',
  possible: 'Revisar coincidencia',
  new: 'Producto nuevo',
};

export default function InvoiceScanner({ products = [], onApply }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ label: '', value: 0 });
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const catalog = useMemo(
    () => products.filter(eligibleProduct).sort((a, b) => String(a.name).localeCompare(String(b.name), 'es')),
    [products]
  );

  useEffect(() => () => {
    previews.forEach(url => URL.revokeObjectURL(url));
  }, [previews]);

  const replaceFiles = (selected) => {
    previews.forEach(url => URL.revokeObjectURL(url));
    const next = Array.from(selected || []).slice(0, MAX_FILES);
    if (selected?.length > MAX_FILES) toast.warn(`Solo se permiten ${MAX_FILES} fotos por factura.`);
    setFiles(next);
    setPreviews(next.map(file => URL.createObjectURL(file)));
    setResult(null);
    setError('');
  };

  const analyze = async () => {
    if (!files.length) {
      setError('Selecciona al menos una foto.');
      return;
    }
    setAnalyzing(true);
    setError('');
    try {
      const invoice = await analyzeInvoiceLocally(files, (label, value) => {
        setProgress({ label, value });
      });
      const rows = (invoice?.items || []).map((item, index) => {
        const match = findMatch(item.description, catalog);
        const quantity = numberValue(item.quantity) || 1;
        const totalCost = numberValue(item.totalCost);
        const unitCost = numberValue(item.unitCost) || (quantity > 0 ? totalCost / quantity : 0);
        return {
          id: `scan-${Date.now()}-${index}`,
          description: String(item.description || '').trim(),
          quantity,
          unit: match.product?.unit || normalizeUnit(item.unit),
          totalCost: Math.round(totalCost),
          unitCost,
          productId: match.product?.id || NEW_PRODUCT,
          status: match.status,
          confidence: numberValue(item.confidence),
          needsReview: !!item.needsReview || match.status === 'possible',
          reviewReason: item.reviewReason || '',
        };
      });
      setResult({
        supplier: invoice?.supplier || '',
        invoiceNumber: invoice?.invoiceNumber || '',
        date: invoice?.date || '',
        declaredTotal: numberValue(invoice?.total),
        overallConfidence: numberValue(invoice?.overallConfidence),
        notes: Array.isArray(invoice?.notes) ? invoice.notes : [],
        rawText: invoice?.rawText || '',
        rows,
      });
    } catch (analysisError) {
      setError(analysisError?.message || 'No se pudo analizar la factura.');
    } finally {
      setAnalyzing(false);
    }
  };

  const updateResult = (field, value) => setResult(prev => ({ ...prev, [field]: value }));
  const updateRow = (index, patch) => setResult(prev => ({
    ...prev,
    rows: prev.rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row),
  }));

  const chooseProduct = (index, productId) => {
    if (productId === NEW_PRODUCT) {
      updateRow(index, { productId, status: 'new', needsReview: false });
      return;
    }
    const product = catalog.find(item => item.id === productId);
    if (!product) return;
    updateRow(index, {
      productId,
      description: product.name,
      unit: product.unit || 'unidad',
      status: 'matched',
      needsReview: false,
      reviewReason: '',
    });
  };

  const updateQuantity = (index, value) => {
    const quantity = numberValue(value);
    const row = result.rows[index];
    updateRow(index, {
      quantity,
      unitCost: quantity > 0 ? numberValue(row.totalCost) / quantity : 0,
    });
  };

  const updateTotal = (index, value) => {
    const totalCost = numberValue(value);
    const row = result.rows[index];
    updateRow(index, {
      totalCost,
      unitCost: numberValue(row.quantity) > 0 ? totalCost / numberValue(row.quantity) : 0,
    });
  };

  const calculatedTotal = result?.rows?.reduce((sum, row) => sum + numberValue(row.totalCost), 0) || 0;
  const difference = result ? Math.round(numberValue(result.declaredTotal) - calculatedTotal) : 0;

  const apply = () => {
    const invalid = result.rows.some(row => !String(row.description).trim() || numberValue(row.quantity) <= 0 || numberValue(row.totalCost) <= 0);
    if (invalid) {
      setError('Revisa los renglones: todos necesitan nombre, cantidad y costo total mayor que cero.');
      return;
    }
    if (difference !== 0 && !window.confirm(`Hay una diferencia de $${money(Math.abs(difference))} entre el total escrito y los renglones. ¿Deseas continuar?`)) {
      return;
    }
    onApply({ ...result, files });
    toast.success('Datos cargados al formulario. Revísalos y registra la entrada cuando estén correctos.');
  };

  const addBlankRow = () => {
    updateResult('rows', [
      ...result.rows,
      {
        id: `manual-${Date.now()}`,
        description: '',
        quantity: 1,
        unit: 'unidad',
        totalCost: 0,
        unitCost: 0,
        productId: NEW_PRODUCT,
        status: 'new',
        confidence: 0,
        needsReview: true,
        reviewReason: 'Renglón agregado para revisión manual.',
      },
    ]);
  };

  return (
    <section className="mb-7 overflow-hidden rounded-3xl border border-sky-200 bg-sky-50/60">
      <div className="border-b border-sky-200 bg-gradient-to-r from-sky-700 to-blue-700 px-5 py-5 text-white">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-sky-100">Carga inteligente</p>
        <h3 className="mt-1 text-xl font-black sm:text-2xl">Leer factura desde una foto</h3>
        <p className="mt-1 text-sm text-sky-100">OCR local sin cobros por análisis. Primero podrás revisar y corregir todo.</p>
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className="flex cursor-pointer items-center justify-center rounded-2xl border-2 border-dashed border-sky-300 bg-white px-5 py-4 text-sm font-bold text-sky-800 transition hover:border-sky-500 hover:bg-sky-50">
            Seleccionar foto(s)
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={event => replaceFiles(event.target.files)} />
          </label>
          <button
            type="button"
            onClick={analyze}
            disabled={!files.length || analyzing}
            className="rounded-2xl bg-sky-700 px-5 py-4 text-sm font-bold text-white transition hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {analyzing ? 'Leyendo factura localmente…' : 'Leer factura sin costo'}
          </button>
          <span className="text-xs text-slate-500">Hasta {MAX_FILES} páginas de la misma factura.</span>
        </div>

        {analyzing && (
          <div className="mt-4 rounded-2xl border border-sky-200 bg-white p-3">
            <div className="flex justify-between text-xs font-semibold text-sky-800"><span>{progress.label || 'Preparando OCR…'}</span><span>{Math.round(progress.value * 100)}%</span></div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-sky-100"><div className="h-full rounded-full bg-sky-600 transition-all" style={{ width: `${Math.round(progress.value * 100)}%` }} /></div>
          </div>
        )}

        {previews.length > 0 && (
          <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
            {previews.map((src, index) => (
              <img key={src} src={src} alt={`Página ${index + 1}`} className="h-28 w-24 flex-none rounded-xl border border-slate-200 bg-white object-cover shadow-sm" />
            ))}
          </div>
        )}

        {error && <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}

        {result && (
          <div className="mt-5 space-y-5">
            <div className="grid gap-3 md:grid-cols-3">
              <label className="text-sm font-semibold text-slate-700">Proveedor
                <input value={result.supplier} onChange={event => updateResult('supplier', event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal" />
              </label>
              <label className="text-sm font-semibold text-slate-700">N.º de factura
                <input value={result.invoiceNumber} onChange={event => updateResult('invoiceNumber', event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal" />
              </label>
              <label className="text-sm font-semibold text-slate-700">Fecha
                <input type="date" value={result.date} onChange={event => updateResult('date', event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-normal" />
              </label>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="min-w-[980px] w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-3 py-3">Producto leído</th>
                    <th className="px-3 py-3">Catálogo</th>
                    <th className="px-3 py-3">Cantidad</th>
                    <th className="px-3 py-3">Unidad</th>
                    <th className="px-3 py-3">Costo total</th>
                    <th className="px-3 py-3">Costo unitario</th>
                    <th className="px-3 py-3">Estado</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.rows.map((row, index) => (
                    <tr key={row.id} className={row.needsReview ? 'bg-amber-50/70' : ''}>
                      <td className="px-3 py-3 align-top">
                        <input value={row.description} onChange={event => updateRow(index, { description: event.target.value })} className="w-56 rounded-lg border border-slate-300 px-2 py-2" />
                        {row.reviewReason && <p className="mt-1 max-w-56 text-xs text-amber-700">{row.reviewReason}</p>}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <select value={row.productId} onChange={event => chooseProduct(index, event.target.value)} className="w-56 rounded-lg border border-slate-300 px-2 py-2">
                          <option value={NEW_PRODUCT}>Crear como producto nuevo</option>
                          {catalog.map(product => <option key={product.id} value={product.id}>{product.name}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-3 align-top"><input type="number" min="0" step="any" value={row.quantity} onChange={event => updateQuantity(index, event.target.value)} className="w-24 rounded-lg border border-slate-300 px-2 py-2" /></td>
                      <td className="px-3 py-3 align-top">
                        <select value={row.unit} disabled={row.productId !== NEW_PRODUCT} onChange={event => updateRow(index, { unit: event.target.value })} className="w-28 rounded-lg border border-slate-300 px-2 py-2 disabled:bg-slate-100">
                          {['unidad', 'paquete', 'caja', 'g', 'kg', 'ml', 'l'].map(unit => <option key={unit}>{unit}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-3 align-top"><input type="number" min="0" step="1" value={row.totalCost} onChange={event => updateTotal(index, event.target.value)} className="w-32 rounded-lg border border-slate-300 px-2 py-2" /></td>
                      <td className="whitespace-nowrap px-3 py-5 align-top font-semibold text-slate-700">${money(row.unitCost)}</td>
                      <td className="px-3 py-4 align-top"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${statusStyle[row.status]}`}>{statusLabel[row.status]}</span></td>
                      <td className="px-3 py-4 align-top"><button type="button" onClick={() => updateResult('rows', result.rows.filter((_, rowIndex) => rowIndex !== index))} className="font-semibold text-red-600 hover:underline">Quitar</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <button type="button" onClick={addBlankRow} className="rounded-xl border border-sky-300 bg-white px-4 py-2 text-sm font-bold text-sky-700 hover:bg-sky-50">+ Agregar renglón</button>
              <details className="w-full rounded-xl border border-slate-200 bg-white p-3 sm:max-w-xl">
                <summary className="cursor-pointer text-sm font-semibold text-slate-700">Ver texto detectado por el OCR</summary>
                <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap text-xs text-slate-600">{result.rawText || 'No se reconoció texto.'}</pre>
              </details>
            </div>

            <div className="grid gap-3 rounded-2xl bg-white p-4 text-sm shadow-sm sm:grid-cols-3">
              <div><span className="text-slate-500">Total leído</span><p className="text-lg font-black text-slate-900">${money(result.declaredTotal)}</p></div>
              <div><span className="text-slate-500">Suma de renglones</span><p className="text-lg font-black text-slate-900">${money(calculatedTotal)}</p></div>
              <div><span className="text-slate-500">Diferencia</span><p className={`text-lg font-black ${difference === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>${money(Math.abs(difference))}</p></div>
            </div>

            {difference !== 0 && <p className="rounded-xl bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-800">El total escrito y la suma de los productos no coinciden. Revisa los valores antes de continuar.</p>}

            <div className="flex justify-end">
              <button type="button" onClick={apply} disabled={!result.rows.length} className="rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">Usar estos datos en la entrada</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
